import axios from "axios";

const API_BASE = "/api/invoice";

export interface InvoiceProductSummary {
    product_uuid: string;
    description: string;
}

export interface InvoiceSummary {
    invoice_uuid: string;
    invoice_number: string;
    issue_date: string;
    currency: string;
    total_minor: number;
    currency_exponent: number;
    products: InvoiceProductSummary[];
    download_available: boolean;
}

export type InvoiceAvailabilityState = "issued" | "legacy_available";

export interface InvoiceAvailabilityItem {
    product_uuid: string;
    state: InvoiceAvailabilityState;
    invoice_uuid: string | null;
    invoice_number: string | null;
}

export interface InvoiceAvailabilityResponse {
    items: InvoiceAvailabilityItem[];
    legacy_checks_degraded: boolean;
}

export const listInvoices = async (): Promise<InvoiceSummary[]> => {
    const response = await axios.get<InvoiceSummary[]>(API_BASE);
    return response.data;
}

export const getInvoiceAvailability = async (): Promise<InvoiceAvailabilityResponse> => {
    const response = await axios.get<InvoiceAvailabilityResponse>(`${API_BASE}/availability`);
    return response.data;
}

export const ensureInvoiceForProduct = async (productUuid: string): Promise<InvoiceSummary> => {
    const response = await axios.post<InvoiceSummary>(
        `${API_BASE}/product/${encodeURIComponent(productUuid)}/ensure`
    );
    return response.data;
}

export const getInvoicePdf = async (invoiceUuid: string): Promise<Blob> => {
    const response = await axios.get<Blob>(
        `${API_BASE}/${encodeURIComponent(invoiceUuid)}/download`,
        { responseType: "blob" }
    );
    return response.data;
}

export const getInvoiceFilename = (invoiceNumber: string): string => {
    const safeInvoiceNumber = invoiceNumber
        .trim()
        .replace(/[^A-Za-z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 100) || "Invoice";

    return `WaveMind-Invoice-${safeInvoiceNumber}.pdf`;
}

export const saveInvoicePdf = (pdf: Blob, invoiceNumber: string): void => {
    const objectUrl = window.URL.createObjectURL(pdf);
    const link = document.createElement("a");

    const cleanup = () => {
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    };

    try {
        link.href = objectUrl;
        link.download = getInvoiceFilename(invoiceNumber);
        link.rel = "noopener";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
    } finally {
        // WebKit may still be resolving the Blob URL when click() returns.
        // Keep the URL alive briefly, but guarantee cleanup on every path.
        window.setTimeout(cleanup, 200);
    }
}

export const downloadInvoicePdf = async (
    invoiceUuid: string,
    invoiceNumber: string
): Promise<void> => {
    const pdf = await getInvoicePdf(invoiceUuid);
    saveInvoicePdf(pdf, invoiceNumber);
}
